"use client"

import { RootState } from "@/(admin)/modules/auth/services/redux/store"
import {
    createStore as apiCreateStore,
    updateStoreById as apiUpdateStore,
    createStoreLocalizationById,
    deleteStoreById,
    fetchStoreById,
    fetchStores
} from "@/(admin)/modules/stores/services/store"
import { STORES } from "@/constants/admin-paths"
import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import { normalizeText } from "@/lib/external-components"
import { BlocksImageWithTitleComponent, BlocksTimeRangeComponent, StoreListResponseDataItem, StoreRequest, StoreResponseDataObject } from "@/types/codegen/data-contracts"
import { App } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import queryString from "query-string"
import { useCallback, useState } from "react"
import { useSelector } from "react-redux"

interface PaginationMeta {
    pagination?: {
        page?: number
        pageSize?: number
        pageCount?: number
        total?: number
    }
}

export const useStoresAdmin = () => {
    const router = useRouter()
    const { message, modal } = App.useApp()
    const account = useSelector((state: RootState) => state.account)

    const [dataItem, setDataItem] = useState<StoreListResponseDataItem[]>([])
    const [loading, setLoading] = useState(false)
    const [metaData, setMetaData] = useState<PaginationMeta>()
    const [totalItem, setTotalItem] = useState(0)
    const [storeOptions, setStoreOptions] = useState<{ value: string | number; label: string }[]>([])
    const [filterTabStore, setFilterTabStore] = useState("all")
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const [bulkAction, setBulkAction] = useState<string>("")
    const [storeTempData, setStoreTempData] = useState<StoreListResponseDataItem>()
    const [needRefresh, setNeedRefresh] = useState<boolean>(false)
    const [storeDetail, setStoreDetail] = useState<StoreResponseDataObject | null>(null);
    const [pagination, setPagination] = useState<{
        page: number
        pageSize: number
        total: number
        pageCount: number
    }>({
        page: 1,
        pageSize: 20,
        total: 0,
        pageCount: 0,
    })
    const searchParams = useSearchParams();
    const locale = searchParams.get("locale")

    const { hasPermission: checkPermission } = useCheckPermission()
    const hasPermission = useCallback((action: string) => {
        return checkPermission("store", action)
    }, [checkPermission]);

    const fetchStoresData = useCallback(
        async (searchTerms?: { [key: string]: string | number | object }) => {
            setLoading(true)
            setDataItem([])
            try {
                const params = {
                    page: String(pagination.page),
                    pageSize: String(pagination.pageSize),
                    ...searchTerms,
                }
                const allResponse = await fetchStores(params)
                const response = allResponse && allResponse.data
                const meta = allResponse && allResponse.meta
                setMetaData(meta)

                if (response && response.length > 0) {
                    setDataItem(response)
                    if (searchTerms?.publicationState === "all") {
                        setTotalItem(meta?.pagination?.total || 0)
                    }
                    setStoreOptions(
                        response.map((career: StoreListResponseDataItem) => ({
                            value: career.id || 0,
                            label: career.attributes?.name || "",
                        })),
                    )
                    setLoading(false)
                } else {
                    setLoading(false)
                }

                if (meta?.pagination) {
                    setPagination({
                        page: meta.pagination.page || 1,
                        pageSize: meta.pagination.pageSize || 20,
                        total: meta.pagination.total || 0,
                        pageCount: meta.pagination.pageCount || 0,
                    })
                }

                return response
            } catch (error) {
                message.error(t("Không thể lấy danh sách cửa hàng. Vui lòng thử lại."))
            }
        },
        [message, pagination.page, pagination.pageSize],
    )
    const getStoreInformation = useCallback(async (id: number) => {
        setLoading(true);
        try {
            const res = await fetchStoreById(id);
            setStoreDetail(res);
            return res;
        } catch (error) {
            // error handling
        } finally {
            setLoading(false);
        }
    }, []);

    const createStore = useCallback(async (data: StoreRequest) => {
        setLoading(true);
        try {
            const res = await apiCreateStore(data);
            message.success(t("Thêm cửa hàng thành công"));
            return res;
        } catch (error) {
            message.error(t("Thêm cửa hàng thất bại"));
            return null;
        } finally {
            setLoading(false);
        }
    }, [message]);

    const updateStore = useCallback(async (id: number, data: StoreRequest) => {
        setLoading(true);
        try {
            const res = await apiUpdateStore(id, data);
            message.success(t("Cập nhật cửa hàng thành công"));
            return res;
        } catch (error) {
            message.error(t("Cập nhật cửa hàng thất bại"));
            return null;
        } finally {
            setLoading(false);
        }
    }, [message]);

    const handleDuplicate = useCallback(async (record: StoreListResponseDataItem) => {
        if (record) {
            setLoading(true);
            const attr = record.attributes;
            const baseName = attr?.name || "";
            const copyName = `${baseName} (Copy)`;

            const cleanManagers = attr?.managers?.map((manager: BlocksImageWithTitleComponent) => ({
                name: manager.name,
                url: manager.url || null,
                target: manager.target || "_self",
                image: manager.image?.data?.id || (typeof manager.image === 'number' ? manager.image : null),
            })) || [];

            const cleanTimeRange = attr?.timeRange?.map((timeRange: BlocksTimeRangeComponent) => ({
                day: timeRange.day,
                from: timeRange.from ? (timeRange.from.length === 5 ? `${timeRange.from}:00` : timeRange.from) : null,
                to: timeRange.to ? (timeRange.to.length === 5 ? `${timeRange.to}:00` : timeRange.to) : null,
            })) || [];

            const dataPayload = {
                data: {
                    name: copyName,
                    address: attr?.address,
                    email: attr?.email,
                    phone: attr?.phone,
                    longitude: attr?.longitude ? Number(attr.longitude) : null,
                    latitude: attr?.latitude ? Number(attr.latitude) : null,
                    googleMapCode: attr?.googleMapCode,
                    guidance: attr?.guidance,
                    description: attr?.description,
                    locale: attr?.locale,
                    publishedAt: null,
                    managers: cleanManagers,
                    image: attr?.image?.data?.id || null,
                    galeryPhotos: attr?.galeryPhotos?.data?.map((img: any) => img.id) || [],
                    campaigns: attr?.campaigns?.data?.map((campaign: any) => campaign.id) || [],
                    timeRange: cleanTimeRange,
                },
            } as StoreRequest;

            try {
                await apiCreateStore(dataPayload);
                message.success(t("Nhân bản cửa hàng thành công!"));

                const currentParams = Object.fromEntries(searchParams.entries());

                fetchStoresData({
                    locale: currentParams.locale,
                    name: currentParams.name,
                    ...currentParams
                });
            } catch (error) {
                message.error(t("Không thể nhân bản cửa hàng. Vui lòng thử lại."));
            } finally {
                setLoading(false);
            }
        }
    }, [apiCreateStore, message, fetchStoresData, filterTabStore, searchParams]);

    const handleDelete = useCallback(
        async (id: number, filterValues?: Record<string, string>, sortPaginationValues?: Record<string, string>) => {
            modal.confirm({
                title: t("Xác nhận xóa"),
                content: t("Bạn có chắc chắn muốn xóa cửa hàng này?"),
                okText: t("Xóa"),
                cancelText: t("Hủy"),
                onOk: async () => {
                    try {
                        setLoading(true)
                        await deleteStoreById(id)
                        const currentParams = Object.fromEntries(searchParams.entries());
                        fetchStoresData({
                            locale: currentParams.locale,
                            name: currentParams.name,
                            ...currentParams
                        });
                        message.success(t("Xóa cửa hàng thành công!"))
                    } catch {
                        message.error(t("Lỗi khi xóa cửa hàng"))
                    }
                },
            })
        },
        [message, filterTabStore, fetchStoresData, locale, searchParams],
    )

    const handleBulkDelete = useCallback(
        async (filterValues?: Record<string, string>, sortPaginationValues?: Record<string, string>) => {
            try {
                setLoading(true)
                const ids = selectedRowKeys.map(id => Number(id))
                await Promise.all(ids.map(id => deleteStoreById(id)))
                setSelectedRowKeys([])
                const currentParams = Object.fromEntries(searchParams.entries());
                fetchStoresData({
                    locale: currentParams.locale,
                    name: currentParams.name,
                    ...currentParams
                });
                setLoading(false)
                message.success(t("Xóa cửa hàng thành công!"))
            } catch {
                message.error(t("Lỗi khi xóa cửa hàng"))
            }
        },
        [selectedRowKeys, message, filterTabStore, fetchStoresData, locale, searchParams],
    )

    const handleApplyBulkAction = useCallback(
        async (filterValues?: Record<string, string>, sortPaginationValues?: Record<string, string>) => {
            if (selectedRowKeys.length === 0) {
                message.warning(t("Vui lòng chọn cửa hàng để thực hiện thao tác."));
                return;
            }

            const currentParams = {
                locale: locale || "vi",
                ...filterValues,
                ...sortPaginationValues,
                publicationState: filterTabStore,
            };

            if (bulkAction === "delete") {
                modal.confirm({
                    title: t("Xác nhận xóa"),
                    content: `${t("Bạn có chắc chắn muốn xóa")} ${selectedRowKeys.length} ${t("cửa hàng đã chọn?")}`,
                    okText: t("Xóa"),
                    cancelText: t("Hủy"),
                    onOk: () => handleBulkDelete(filterValues, sortPaginationValues),
                });
                return;
            }

            if (bulkAction === "publish" || bulkAction === "draft") {
                try {
                    setLoading(true);
                    const isPublish = bulkAction === "publish";
                    const updatePayload = {
                        data: {
                            publishedAt: isPublish ? new Date().toISOString() : null,
                        }
                    };

                    await Promise.all(
                        selectedRowKeys.map(id => apiUpdateStore(Number(id), updatePayload as any))
                    );

                    message.success(`${isPublish ? t("Xuất bản") : t("Chuyển về nháp")} ${t("hàng loạt thành công!")}`);

                    setSelectedRowKeys([]);
                    fetchStoresData(currentParams);
                } catch (error) {
                    console.error("Bulk action error:", error);
                    message.error(`Lỗi khi thực hiện thao tác ${bulkAction === "publish" ? t("xuất bản") : t("chuyển về nháp")}`);
                } finally {
                    setLoading(false);
                }
                return;
            }

            message.warning(t("Vui lòng chọn một thao tác hàng loạt hợp lệ."));
        },
        [bulkAction, selectedRowKeys, message, handleBulkDelete, fetchStoresData, filterTabStore, apiUpdateStore, locale]
    );

    const handleEdit = useCallback(
        (id: number) => {
            router.push(`/admin/${STORES.EDIT.PATH}?id=${id}`)
        },
        [router],
    )

    const handleRefresh = useCallback(
        (filterRef?: { current: { filterFunction: () => void } | null }) => {
            if (filterRef?.current) {
                filterRef.current.filterFunction()
            }
            setNeedRefresh(!needRefresh)
        },
        [needRefresh],
    )

    const handleSearch = useCallback(
        (filters: object, pathname: string) => {
            const values: { [key: string]: string } = {}

            Object.keys(filters).forEach(key => {
                const value = filters[key as keyof typeof filters]
                if (typeof value === "string") {
                    values[key] = value
                }
            })

            if (!values) {
                message.error("Giá trị không hợp lệ")
                return
            }

            let newValue = { ...values }

            if (values.search) {
                newValue = { ...newValue, search: normalizeText(values.search) }
            }

            const searchParams = queryString.stringify(newValue, { encode: false })
            const newUrl = searchParams.length > 0 ? `${pathname}?${searchParams}` : pathname
            router.push(`${newUrl}`)
        },
        [router, message],
    )

    const handleCreateStoreLocalization = async (id: number, data: any) => {
        setLoading(true);
        try {
            const res = await createStoreLocalizationById(id, data);
            if (res) {

                return res;
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        dataItem,
        loading,
        metaData,
        totalItem,
        storeOptions,
        filterTabStore,
        selectedRowKeys,
        bulkAction,
        storeTempData,
        needRefresh,
        storeDetail,
        pagination,

        actions: {
            fetchStoresData,
            getStoreInformation,
            handleDelete,
            handleEdit,
            handleRefresh,
            handleSearch,
            setSelectedRowKeys,
            setBulkAction,
            setFilterTabStore,
            handleDuplicate,
            handleApplyBulkAction,
            handleBulkDelete,
            hasPermission,
            updateStore,
            createStore,
            handleCreateStoreLocalization,
            setPagination,
        },

        setters: {
            setDataItem,
            setLoading,
            setMetaData,
            setTotalItem,
            setStoreOptions,
            setSelectedRowKeys,
            setBulkAction,
            setStoreTempData,
            setNeedRefresh,
        },
    }
}